import React, { memo } from "react";

import { useField } from "formik";
import * as Yup from "yup";

import DeploymentBusinessTable from "components/Business/BusinessForm/DeploymentBusinessTable";
import { withNamespace } from "components/Form/FormikControls";

import { EmailRecipientsHeader } from "./EmailRecipientsHeader";
import { EmailRecipientsPanel } from "./EmailRecipientsPanel";

const emailRecipientBaseValidation: Yup.SchemaOf<
  Omit<EmailRecipient, "id" | "source">
> = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  email: Yup.string().required("Required").email("Invalid email"),
  phoneNumber: Yup.string(),
  isAccountsRecipient: Yup.boolean(),
  isCommercialReportRecipient: Yup.boolean(),
  isComplianceReportRecipient: Yup.boolean(),
  isEnvdRecipient: Yup.boolean(),
});

const emailRecipientValidationFactory = () =>
  Yup.lazy(value =>
    Yup.object().shape({
      editingValue: emailRecipientBaseValidation
        .shape({
          email: Yup.string()
            .email("Invalid Email")
            .required("Required")
            .test(
              "is-email-unique",
              "That email already exists",
              emailValue => {
                // Test the value in the email field against
                //   - the other EmailRecipients in the dirty Formik state
                // ignore BusinessUsers - allow local version of BusinessUser email
                const emailIdPairList = [...value.values].map(
                  ({ email, id }) => ({ email, id }),
                );

                return (
                  // When there is an email address entered
                  Boolean(emailValue) &&
                  emailIdPairList
                    // Find all of the emails that match the entered value
                    .filter(emailId => emailId.email === emailValue.trim())
                    // and check that every match is the existing entry
                    .every(emailId => emailId.id === value.editingValue.id)
                );
              },
            ),
        })
        .nullable(),
    }),
  );

interface EmailRecipientsProps {
  masterBusinessId: string;
  primaryContactEmail: string;
  namespace: string;
  isOpen: boolean;
  onEndEditing: () => void;
  onStartEditing: () => void;
  onSetPrimaryContactEmail: (emailAddress: string) => void;
  onToggle: () => void;
}

const EmailRecipientsComponent: React.FC<EmailRecipientsProps> = ({
  masterBusinessId,
  primaryContactEmail,
  namespace: ns,
  isOpen,
  onEndEditing,
  onStartEditing,
  onSetPrimaryContactEmail,
  onToggle,
}) => {
  const emailRecipientsNs = withNamespace(ns, "emailRecipients");
  const emailRecipients = useField(emailRecipientsNs)[0].value;

  return (
    <DeploymentBusinessTable
      headerComponent={EmailRecipientsHeader}
      initialValuesList={emailRecipients}
      isOpen={isOpen}
      masterBusinessId={masterBusinessId}
      primaryContactEmail={primaryContactEmail}
      namespace={emailRecipientsNs}
      onEndEditing={onEndEditing}
      onStartEditing={onStartEditing}
      onSetPrimaryContactEmail={onSetPrimaryContactEmail}
      onToggle={onToggle}
      tableComponent={EmailRecipientsPanel}
      validationSchema={emailRecipientValidationFactory}
      dataTour="emailCollapse"
    />
  );
};

export default memo(EmailRecipientsComponent);
